import { Vector2, Vector3, Quaternion, Euler } from 'three'
import { AngleRelation, Bounds, ObjectRelation } from '../models/Types';
import { Vector3 as BabylonVector3, Quaternion as BabylonQuaternion } from '@babylonjs/core';

export function toDegrees(angleInRadians: number) {
  return angleInRadians * 180 / Math.PI;
}

export function toRadians(angleInDegrees: number) {
  return angleInDegrees * Math.PI / 180;
}

export function addVector2(v1: Vector2, v2: Vector2): Vector2 {
  const result = new Vector2(v1.x + v2.x, v1.y + v2.y);
  return result;
}

export function getDistance(v1: Vector3, v2: Vector3): number {
  const x = v2.x - v1.x;
  const x2 = Math.pow(x, 2);
  const y = v2.y - v1.y;
  const y2 = Math.pow(y, 2);
  const z = v2.z - v1.z;
  const z2 = Math.pow(z, 2);
  const result = Math.sqrt(x2 + y2 + z2);
  return result;
}

export const defaultScaleForWorldExtend = 0.2;

export const zeroBounds: Bounds = {
  min: new Vector3(0,0,0),
  max: new Vector3(0,0,0),
};

export function getSizeForWorld(extend: any, scale: number = defaultScaleForWorldExtend ): { width: number, depth: number } {
  const width = (extend.max.x - extend.min.x) * scale;
  const depth = (extend.max.z - extend.min.z) * scale;
  return { width, depth };
}

export function getBoundsForWorld(extend: any, scale: number = defaultScaleForWorldExtend ): Bounds {
  const size = getSizeForWorld(extend, scale/2);
  return {
    min: new Vector3(0 - size.width, 0, 0 - size.depth),
    max: new Vector3(0 + size.width, 0, 0 + size.depth),
  };
}

export function clamp(value: number, min: number, max: number): number {
  if (value >= max) return max;
  if (value <= min) return min;
  return value;
}

export function calculateAngleInfoFromSourceToTarget(sourcePosition: Vector3, sourceRotation: Euler, targetPosition: Vector3): AngleRelation {
  const directionToTarget = targetPosition.clone().sub(sourcePosition).normalize();

  const quaternion = new Quaternion().setFromEuler(sourceRotation);

  const forwardDirection = new Vector3(0, 0, -1).applyQuaternion(quaternion);

  const dotProduct = clamp(forwardDirection.dot(directionToTarget), -1, 1);
  const crossProduct = new Vector3().crossVectors(forwardDirection, directionToTarget);

  const shortestAngleRadians = Math.acos(dotProduct);
  let clockwiseAngleRadians = shortestAngleRadians;

  const isPanningLeft: boolean = crossProduct.dot(new Vector3(0, 1, 0)) < 0;
  if (isPanningLeft) {
    clockwiseAngleRadians = Math.PI * 2 - clockwiseAngleRadians;
  }

  return { shortestAngleRadians, clockwiseAngleRadians };
}

export function calculateBabylonAngleInfoFromSourceToTarget(sourcePosition: BabylonVector3, sourceRotation: BabylonVector3, targetPosition: BabylonVector3): AngleRelation {
  const directionToTarget = targetPosition.clone().subtract(sourcePosition).normalize();

  // Convert the source rotation vector to a quaternion
  const quaternion = BabylonQuaternion.FromEulerAngles(sourceRotation.x, sourceRotation.y, sourceRotation.z);

  // Transform the forward direction by the rotation quaternion
  const forwardDirection = BabylonVector3.Forward().rotateByQuaternionToRef(quaternion, BabylonVector3.Zero());

  const dotProduct = clamp(BabylonVector3.Dot(forwardDirection, directionToTarget), -1, 1);
  const crossProduct = BabylonVector3.Cross(forwardDirection, directionToTarget);

  const shortestAngleRadians = Math.acos(dotProduct);
  let clockwiseAngleRadians = shortestAngleRadians;

  const isPanningLeft: boolean = BabylonVector3.Dot(crossProduct, BabylonVector3.Up()) < 0;
  if (isPanningLeft) {
      clockwiseAngleRadians = Math.PI * 2 - clockwiseAngleRadians;
  }

  return { shortestAngleRadians, clockwiseAngleRadians };
}

export function toVector3(value: BabylonVector3) {
  return new Vector3(value.x, value.y, value.z);
}

export function toEuler(value: BabylonVector3) {
  return new Euler(value.x, value.y, value.z, 'XYZ');
}

export function angleBetweenAngles(angle1: number, angle2: number): number {
  return Math.abs(angle1 - angle2) % (Math.PI * 2);
}

export function calculateLinearGradient(x1: number, x2: number, y1: number, y2: number) {
  return (y2-y1)/(x2-x1);
}

export function interpolate(x: number, xMin: number, xMax: number, yAtMin: number, yAtMax: number) {
  x = clamp(x, xMin, xMax);
  if (xMin === xMax) {
    console.error('interpolate xMin and xMax equal, defaulting to yMin');
    return yAtMin;
  }
  const interpolationFactor = (x - xMin) / (xMax - xMin);
  const interpolatedValue = yAtMin + interpolationFactor * (yAtMax - yAtMin);
  return interpolatedValue;
}

export function smoothInterpolate(x: number, xMin: number, xMax: number, yAtMin: number, yAtMax: number, xOffset: number, yInitial: number) {
  x = clamp(x, xMin, xMax);
  xOffset = clamp(xOffset, xMin, xMax);
  if (x < xOffset) {
    return interpolate(x, xMin, xOffset, yInitial, yAtMin);
  } else {
    return interpolate(x, xOffset, xMax, yAtMin, yAtMax);
  }
}

export const calculateRelationDetailsForVectors = (sourcePosition: Vector3, sourceRotation: Euler, targetPosition: Vector3, targetRotation: Euler): ObjectRelation => {
  const distance = getDistance(sourcePosition, targetPosition);
  const sourceToTarget = calculateAngleInfoFromSourceToTarget(sourcePosition, sourceRotation, targetPosition);
  const targetToSource = calculateAngleInfoFromSourceToTarget(targetPosition, targetRotation, sourcePosition);

  const result: ObjectRelation = {
    distance,
    sourceToTarget,
    targetToSource
  };

  return result;
}
